import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { ArrowLeft, ClipboardList, Loader2 } from "lucide-react";

export const RapidIntakePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [formData, setFormData] = useState({
    // Business Context
    current_role: "",
    business_model: "",
    growth_stage: "",
    // Execution Reality
    lagging_results: "",
    heavy_decisions: "",
    delayed_matters: "",
    // Leadership Capacity
    next_level_role: "",
    behavior_gaps: "",
    trust_decisions: "",
    // Disclaimer
    disclaimer_accepted: false
  });

  useEffect(() => {
    fetchEnrollment();
  }, []);

  const fetchEnrollment = async () => {
    try {
      const response = await authFetch(`${API}/rapid/enrollment`, {  });
      if (response.ok) {
        const data = await response.json();
        setEnrollment(data);
        // Pre-fill if intake already exists
        if (data.intake_data) {
          setFormData(prev => ({ ...prev, ...data.intake_data }));
        }
      }
    } catch (_error) {
      console.error("Error fetching enrollment:", _error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.disclaimer_accepted) {
      toast.error("You must accept the disclaimer to continue");
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`${API}/rapid/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Intake submitted successfully!");
        navigate("/rapid");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit intake");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="rapid-intake-page">
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/rapid")} className="text-neutral-600">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-neutral-900">Leadership & Decision Intake</h1>
              <p className="text-sm text-neutral-500">R.A.P.I.D.™ Business Recalibration</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Business Context */}
          <Card className="border-neutral-200">
            <CardHeader className="bg-emerald-50 border-b border-emerald-100">
              <CardTitle className="text-lg flex items-center">
                <ClipboardList className="w-5 h-5 mr-2 text-emerald-600" />
                Business Context
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Current role/title</Label>
                <Input
                  value={formData.current_role}
                  onChange={(e) => updateField("current_role", e.target.value)}
                  placeholder="e.g., CEO, Founder, Managing Director"
                  required
                  data-testid="intake-role"
                />
              </div>
              <div className="space-y-2">
                <Label>Business model</Label>
                <Input
                  value={formData.business_model}
                  onChange={(e) => updateField("business_model", e.target.value)}
                  placeholder="e.g., B2B SaaS, Consulting, E-commerce"
                  required
                  data-testid="intake-model"
                />
              </div>
              <div className="space-y-2">
                <Label>Current growth stage</Label>
                <Input
                  value={formData.growth_stage}
                  onChange={(e) => updateField("growth_stage", e.target.value)}
                  placeholder="e.g., Startup, Growth, Scale, Mature"
                  required
                  data-testid="intake-stage"
                />
              </div>
            </CardContent>
          </Card>

          {/* Execution Reality */}
          <Card className="border-neutral-200">
            <CardHeader className="bg-blue-50 border-b border-blue-100">
              <CardTitle className="text-lg">Execution Reality</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Where are results lagging despite effort?</Label>
                <Textarea
                  value={formData.lagging_results}
                  onChange={(e) => updateField("lagging_results", e.target.value)}
                  placeholder="Describe areas where you're putting in work but not seeing proportional results..."
                  className="min-h-24"
                  required
                  data-testid="intake-lagging"
                />
              </div>
              <div className="space-y-2">
                <Label>Which decisions feel heavier than they should?</Label>
                <Textarea
                  value={formData.heavy_decisions}
                  onChange={(e) => updateField("heavy_decisions", e.target.value)}
                  placeholder="What decisions are consuming more mental energy than their importance warrants..."
                  className="min-h-24"
                  required
                  data-testid="intake-heavy"
                />
              </div>
              <div className="space-y-2">
                <Label>What are you delaying that you know matters?</Label>
                <Textarea
                  value={formData.delayed_matters}
                  onChange={(e) => updateField("delayed_matters", e.target.value)}
                  placeholder="List the important decisions or actions you've been putting off..."
                  className="min-h-24"
                  required
                  data-testid="intake-delayed"
                />
              </div>
            </CardContent>
          </Card>

          {/* Leadership Capacity */}
          <Card className="border-neutral-200">
            <CardHeader className="bg-purple-50 border-b border-purple-100">
              <CardTitle className="text-lg">Leadership Capacity</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>What does your next-level role require?</Label>
                <Textarea
                  value={formData.next_level_role}
                  onChange={(e) => updateField("next_level_role", e.target.value)}
                  placeholder="Describe the capabilities, mindset, and behaviors required for your next level..."
                  className="min-h-24"
                  required
                  data-testid="intake-next-level"
                />
              </div>
              <div className="space-y-2">
                <Label>Where do your current behaviors fall short of that role?</Label>
                <Textarea
                  value={formData.behavior_gaps}
                  onChange={(e) => updateField("behavior_gaps", e.target.value)}
                  placeholder="Be honest about the gaps between where you are and where you need to be..."
                  className="min-h-24"
                  required
                  data-testid="intake-gaps"
                />
              </div>
              <div className="space-y-2">
                <Label>What would change if you trusted your decisions fully?</Label>
                <Textarea
                  value={formData.trust_decisions}
                  onChange={(e) => updateField("trust_decisions", e.target.value)}
                  placeholder="Imagine operating with complete confidence in your decisions..."
                  className="min-h-24"
                  required
                  data-testid="intake-trust"
                />
              </div>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="disclaimer"
                  checked={formData.disclaimer_accepted}
                  onCheckedChange={(checked) => updateField("disclaimer_accepted", checked)}
                  data-testid="intake-disclaimer"
                />
                <label htmlFor="disclaimer" className="text-sm text-neutral-700 cursor-pointer">
                  <span className="font-medium">Required Acknowledgment:</span> I understand this engagement is business consulting and does not replace therapy or mental health care. The focus is on business decisions, leadership execution, and operational outcomes.
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end space-x-4">
            <Button type="button" variant="outline" onClick={() => navigate("/rapid")}>
              Save & Exit
            </Button>
            <Button 
              type="submit" 
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={loading || !formData.disclaimer_accepted}
              data-testid="intake-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Intake"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RapidIntakePage;
